import React from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, SectionCard, CardSkeleton, EmptyState } from "@/admin/ui";

export default function AdminLegal() {
  const { t } = useLang();
  const [items, setItems] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [form, setForm] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = () => api.get("/api/admin/legal").then((r) => setItems(r.data.items));
  React.useEffect(() => { load(); }, []);
  React.useEffect(() => {
    if (selected) {
      setForm({
        title_en: selected.title_en, title_tr: selected.title_tr,
        body_en: selected.body_en, body_tr: selected.body_tr,
        published: !!selected.published, bump_version: false,
      });
    }
  }, [selected]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put(`/api/admin/legal/${selected.slug}`, { slug: selected.slug, ...form }); toast.success(t("common.saved")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-legal-page">
      <PageHeader title={t("admin.legal.title")} description={t("admin.legal.subtitle")} />
      {!items ? <CardSkeleton h="h-64" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          <SectionCard title={`${items.length} documents`} padding="p-2">
            <div className="max-h-[70vh] overflow-auto space-y-0.5">
              {items.map((d) => (
                <button key={d.slug} onClick={() => setSelected(d)}
                  className={`lp-sidebar-link w-full text-left ${selected?.slug === d.slug ? "active" : ""}`}
                  data-testid={`legal-doc-${d.slug}`}>
                  <span className="min-w-0">
                    <div className="text-sm truncate">{d.title_en}</div>
                    <div className="text-[10px] text-white/40 font-mono">v{d.version} · {d.published ? "published" : "draft"}</div>
                  </span>
                </button>
              ))}
            </div>
          </SectionCard>

          {!selected ? <EmptyState title="Select a document from the left." /> : (
            <SectionCard title={selected.slug} description={`${t("admin.legal.current_version")}: v${selected.version}`}>
              <form onSubmit={save} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.legal.title_en")}</label>
                    <input className="lp-input" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="legal-title-en" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.legal.title_tr")}</label>
                    <input className="lp-input" value={form.title_tr} onChange={(e) => setForm({ ...form, title_tr: e.target.value })} data-testid="legal-title-tr" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.legal.body_en")}</label>
                  <textarea className="lp-input font-mono text-sm min-h-[220px]" value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} data-testid="legal-body-en" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("admin.legal.body_tr")}</label>
                  <textarea className="lp-input font-mono text-sm min-h-[220px]" value={form.body_tr} onChange={(e) => setForm({ ...form, body_tr: e.target.value })} data-testid="legal-body-tr" />
                </div>
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="legal-published" />
                  <span className="text-white/70 text-sm">{t("admin.legal.published")}</span>
                </label>
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={form.bump_version} onChange={(e) => setForm({ ...form, bump_version: e.target.checked })} data-testid="legal-bump" />
                  <span className="text-white/70 text-sm">{t("admin.legal.bump")}</span>
                </label>
                <div className="flex justify-end">
                  <button type="submit" disabled={busy} className="lp-btn-primary" data-testid="legal-save">{t("admin.legal.save")}</button>
                </div>
              </form>
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
}
